import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { TextareaInput } from "@/components/ui/Field";
import { toast } from "@/lib/toast";
import { getMailSignature, saveMailSignature } from "../../lib/settingsApi";

export function MailSignatureSection() {
  const [html, setHtml] = useState("");
  const [preview, setPreview] = useState("");
  const [placeholders, setPlaceholders] = useState<Array<{ key: string; desc?: string }>>([]);
  const [defaultTemplate, setDefaultTemplate] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMailSignature();
      setHtml(data.signature_html || "");
      setPreview(data.preview_html || "");
      setPlaceholders(data.placeholders || []);
      setDefaultTemplate(data.default_template || "");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Signatur konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  async function handleSave() {
    setSaving(true);
    try {
      await saveMailSignature(html);
      toast.success("Signatur gespeichert.");
      await reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  function loadDefault() {
    if (defaultTemplate) setHtml(defaultTemplate);
  }

  return (
    <Card
      title="E-Mail-Signatur"
      description="Globale HTML-Vorlage für alle Benutzer. Platzhalter wie {{user_display_name}} werden beim Versand mit den persönlichen Werten des jeweiligen Absenders befüllt."
      actions={
        <div className="flex gap-2">
          {defaultTemplate && (
            <Button type="button" variant="secondary" onClick={loadDefault}>
              Standard-Vorlage
            </Button>
          )}
          <Button type="button" onClick={handleSave} loading={saving}>
            Speichern
          </Button>
        </div>
      }
    >
      {loading && <Spinner />}
      {!loading && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <TextareaInput
            label="Signatur (HTML)"
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            rows={14}
            className="font-mono text-xs"
          />
          <div>
            <p className="eh-label mb-2">Vorschau</p>
            <iframe
              title="Signatur-Vorschau"
              className="h-64 w-full rounded-ios border border-separator bg-white"
              srcDoc={preview || html}
            />
            {placeholders.length > 0 && (
              <ul className="mt-3 text-xs text-label-secondary">
                {placeholders.map((p) => (
                  <li key={p.key}><code>{p.key}</code>{p.desc ? ` — ${p.desc}` : ""}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
